// Super-admin impersonation session helpers (backend POST
// /admin/platform/leagues/:id/impersonate). Kept out of AuthContext.tsx so
// that file only exports components and hooks (react-refresh lint rule).

import type { User } from './AuthContext'

/**
 * A super-admin impersonation session. The token's `imp` claim is the banner
 * flag: while it is present the admin shell shows a banner saying who is
 * really signed in, and when the session ends.
 */
export interface ImpersonationInfo {
  byEmail: string
  leagueId: number
  expiresAt: number // ms
}

interface ImpersonationClaims {
  userId?: number
  email?: string
  exp?: number
  leagues?: User['leagues']
  imp?: { by?: number; by_email?: string; league_id?: number; banner?: boolean }
}

// JWT payload decode (no verification; the server verifies every request).
function decodeClaims(token: string): ImpersonationClaims | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const claims = JSON.parse(json)
    return claims && typeof claims === 'object' ? (claims as ImpersonationClaims) : null
  } catch {
    return null
  }
}

export function getImpersonation(token: string | null): ImpersonationInfo | null {
  if (!token) return null
  const decoded = decodeClaims(token)
  const imp = decoded?.imp
  if (!imp || !imp.banner || typeof imp.league_id !== 'number') return null
  return { byEmail: imp.by_email ?? 'a ScoreLM super-admin', leagueId: imp.league_id, expiresAt: (decoded?.exp ?? 0) * 1000 }
}

/** Build the stored user from an impersonation token's claims. */
export function userFromImpersonationToken(token: string): User | null {
  const decoded = decodeClaims(token)
  if (!decoded || !getImpersonation(token) || typeof decoded.userId !== 'number' || typeof decoded.email !== 'string') {
    return null
  }
  return {
    id: decoded.userId,
    email: decoded.email,
    role: 'admin',
    name: null,
    platform_role: null,
    leagues: decoded.leagues ?? [],
  }
}
